import type { ImageSpec, SpreadLayoutId, TextBlock } from '@kitup/book-schema';
import { request } from './client.js';

export type IllustrationStatus = 'draft' | 'queued' | 'generating' | 'ready' | 'failed';

export interface IllustrationVariantDto {
  readonly id: string;
  readonly assetId: string;
  readonly generationId: string | null;
  readonly seed: string | null;
  readonly modelId: string;
  readonly promptSnapshot: string;
  readonly imageSpec: ImageSpec | null;
  readonly url: string;
  readonly urlExpiresAt: string;
  readonly createdAt: string;
}

export interface IllustrationSceneDto {
  readonly id: string;
  readonly bookId: string;
  readonly index: number;
  readonly layoutId: SpreadLayoutId | null;
  readonly textBlocks: readonly TextBlock[];
  readonly bandImageRatio: number | null;
  readonly promptEn: string;
  readonly locationId: string | null;
  readonly imageAssetId: string | null;
  readonly imageSpec: ImageSpec | null;
  readonly imageStale: boolean;
  readonly status: IllustrationStatus;
  readonly lastError: string | null;
  readonly promptStatus: 'queued' | 'running' | 'ready' | 'failed';
  readonly promptError: string | null;
  /** Последнее обращение к модели: код для поддержки (E16-T3). */
  readonly lastGenerationId: string | null;
  readonly variants: readonly IllustrationVariantDto[];
}

export interface IllustrationsDto {
  readonly items: readonly IllustrationSceneDto[];
  readonly replacements: {
    readonly used: number;
    readonly included: number;
    readonly extra: number;
    readonly unlimited: boolean;
    readonly remaining: number;
  };
  readonly progress: {
    readonly total: number;
    readonly queued: number;
    readonly generating: number;
    readonly ready: number;
    readonly failed: number;
    readonly estimatedSeconds: number;
  };
}

export interface CoverDto {
  readonly bookId: string;
  readonly coverAssetId: string | null;
  readonly status: IllustrationStatus;
  readonly lastError: string | null;
  readonly variants: readonly IllustrationVariantDto[];
  readonly backCoverAssetId: string | null;
  readonly backStatus: IllustrationStatus;
  readonly backLastError: string | null;
  readonly backVariants: readonly IllustrationVariantDto[];
}

export const illustrationsApi = {
  list: (bookId: string) => request<IllustrationsDto>(`/books/${bookId}/illustrations`),
  generateAll: (bookId: string) =>
    request<{
      items: readonly {
        sceneId: string;
        generationId: string | null;
        status: 'queued' | 'ready' | 'failed';
        error: string | null;
      }[];
    }>(`/books/${bookId}/scenes/generate`, { method: 'POST' }),
  regenerate: (sceneId: string) =>
    request<{ generationId: string; status: 'queued' }>(`/scenes/${sceneId}/regenerate`, {
      method: 'POST',
    }),
  edit: (sceneId: string, guidance: string, referenceAssetId?: string) =>
    request<{ generationId: string; status: 'queued' }>(`/scenes/${sceneId}/edit`, {
      method: 'POST',
      body: { guidance, ...(referenceAssetId ? { referenceAssetId } : {}) },
    }),
  selectSceneVariant: (sceneId: string, variantId: string) =>
    request<IllustrationSceneDto>(`/scenes/${sceneId}/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
  cover: (bookId: string) => request<CoverDto>(`/books/${bookId}/cover`),
  generateCover: (bookId: string) =>
    request<{ generationId: string; status: 'queued' }>(`/books/${bookId}/cover/generate`, {
      method: 'POST',
    }),
  editCover: (bookId: string, variantId: string, guidance: string) =>
    request<{ generationId: string; status: 'queued' }>(`/books/${bookId}/cover/edit`, {
      method: 'POST',
      body: { variantId, guidance },
    }),
  generateBackCover: (bookId: string) =>
    request<{ generationId: string; status: 'queued' }>(`/books/${bookId}/back-cover/generate`, {
      method: 'POST',
    }),
  editBackCover: (bookId: string, variantId: string, guidance: string) =>
    request<{ generationId: string; status: 'queued' }>(`/books/${bookId}/back-cover/edit`, {
      method: 'POST',
      body: { variantId, guidance },
    }),
  selectCoverVariant: (bookId: string, variantId: string) =>
    request<CoverDto>(`/books/${bookId}/cover/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
  selectBackCoverVariant: (bookId: string, variantId: string) =>
    request<CoverDto>(`/books/${bookId}/back-cover/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
};
